import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { OverlayService } from '../../../../floor-container/floor/item/overlay.service';
import { FloorOption } from '../../elevators/elevators.model';
import { StackingPlanService } from '../../../../../stacking-plan.service';
import { ConflictSpaces } from '../floors.models';
import { MatSelectChange } from '@angular/material/select';
import { EDPFloorSpaceInfo } from '../../../../floor-container/floor/floor.model';
import { EDPSpaceInfo } from '../../../../floor-container/floor/item/item.model';
import { EntityType, isTenancyActive } from '@miq/domain';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'sp-delete-floor-selected',
  templateUrl: './delete-floor-selected.component.html',
  styleUrls: ['./delete-floor-selected.component.scss'],
  standalone: false,
})
export class DeleteFloorSelectedComponent implements OnInit {
  @Output() overlayEmit = new EventEmitter();

  data: number[];
  elevators: number[];
  floorsAboveGround: number;
  floorsBelowGround: number;
  deleteFloorNumber: number;
  floorNumber: string;
  conflictsFound = true;
  private readonly selectionChange$ = new Subject<{ activeConflicts: ConflictSpaces[]; isAboveGround: boolean }>();
  deleteFloorsTypeList: FloorOption[] = [
    {
      label: 'moreInfo.deleteFloorsAbove',
      value: 'above',
    },
    {
      label: 'moreInfo.deleteFloorsBelow',
      value: 'below',
    },
  ];

  constructor(
    private overlayService: OverlayService,
    private stackingPlanService: StackingPlanService
  ) {
    this.selectionChange$
      .pipe(
        untilDestroyed(this),
        switchMap(({ activeConflicts, isAboveGround }) =>
          forkJoin([
            this.stackingPlanService
              .getInactiveRecordsByProperty(EntityType.Availability)
              .pipe(catchError(() => of({ totalCount: 0, results: [] }))),
            this.stackingPlanService
              .getInactiveRecordsByProperty(EntityType.LeaseComp)
              .pipe(catchError(() => of({ totalCount: 0, results: [] }))),
          ]).pipe(
            map(([availabilityResponse, leaseResponse]) => {
              const allInactiveConflicts = [
                ...this.buildInactiveConflictSpaces(availabilityResponse?.results ?? [], EntityType.Availability),
                ...this.buildInactiveConflictSpaces(leaseResponse?.results ?? [], EntityType.LeaseComp),
              ];
              const mergedWithInactive = this.mergeConflictSpaces(activeConflicts, allInactiveConflicts);
              const dynamicFloors = this.buildElevatorOnlyFloors(mergedWithInactive, isAboveGround);
              const conflictsFiltered = [
                ...this.filterFloors(mergedWithInactive, isAboveGround),
                ...dynamicFloors,
              ].sort((a, b) => a.floorIndex - b.floorIndex);
              return { conflictsFiltered, isAboveGround };
            })
          )
        )
      )
      .subscribe(({ conflictsFiltered, isAboveGround }) => {
        this.conflictsFound = !!conflictsFiltered.length;
        this.emitData(isAboveGround, conflictsFiltered.length ? conflictsFiltered : null, false);
        if (conflictsFiltered.length) {
          this.overlayService.disposeBox();
        }
      });
  }

  ngOnInit() {
    const { floors, floorsAboveGround, floorsBelowGround, elevators } = this.overlayService.overlayDialogData;

    this.data = floors.map(floor => floor.floorIndex).sort((a, b) => a - b);
    this.elevators = elevators;
    this.floorsAboveGround = floorsAboveGround;
    this.floorsBelowGround = floorsBelowGround * -1;
  }

  saveDelete(): void {
    this.emitData(this.deleteFloorNumber > 0, null, true);
    this.overlayService.disposeBox();
  }

  floorDeletionChange({ value: selectedDelOpType }: MatSelectChange): void {
    const isAboveGround = selectedDelOpType === 'above';
    this.deleteFloorNumber = isAboveGround ? this.floorsAboveGround : this.floorsBelowGround;
    const lastFloor = this.data.at(isAboveGround ? -1 : 0);
    if (Number.isNaN(this.deleteFloorNumber) || this.deleteFloorNumber === lastFloor || this.deleteFloorNumber === 0) {
      this.conflictsFound = true;
      return;
    }

    this.conflictsFound = true;

    const floorList = this.stackingPlanService.floors;
    const conflictSpaces = floorList.map(item => this.generateConflictFloors(item));
    const pendingIssuesSpaces = this.buildPendingIssueConflicts();
    const activeConflicts = this.mergeConflictSpaces(conflictSpaces || [], pendingIssuesSpaces);

    this.selectionChange$.next({ activeConflicts, isAboveGround });
  }

  cancel(): void {
    this.overlayService.disposeBox();
  }

  private emitData(isAboveGround: boolean, conflictSpaces: ConflictSpaces[], callApi: boolean): void {
    this.overlayEmit.emit({
      floorNumber: this.deleteFloorNumber,
      isAboveGround,
      conflictSpaces,
      callApi,
    });
  }

  private buildPendingIssueConflicts(): ConflictSpaces[] {
    return this.stackingPlanService.pendingIssues
      .filter(({ floor, suite, status }) => status.error && floor.value && suite.value)
      .map(fIssue => {
        const partialFloor: Partial<EDPFloorSpaceInfo> = {
          floor_number: fIssue.floor.value as string,
          spaces: [this.stackingPlanService.issueToSpace(fIssue) as EDPSpaceInfo],
        };
        return this.generateConflictFloors(partialFloor, true);
      });
  }

  private filterFloors(floors: ConflictSpaces[], isAboveGround: boolean): ConflictSpaces[] {
    return floors.filter(floor => {
      const { floorIndex, spaces, elevatorStop } = floor;
      if (isAboveGround) {
        return (
          (floorIndex > this.deleteFloorNumber && spaces.length > 0) ||
          (floorIndex > this.deleteFloorNumber && elevatorStop)
        );
      }
      return (
        (floorIndex < this.deleteFloorNumber && spaces.length > 0) ||
        (floorIndex < this.deleteFloorNumber && elevatorStop)
      );
    });
  }

  private generateConflictFloors(data: Partial<EDPFloorSpaceInfo>, hasPendingIssue?: boolean): ConflictSpaces {
    // remove port number from url
    const portNumberIndex = this.stackingPlanService.baseUrlLocal.lastIndexOf(':');
    const baseURL = this.stackingPlanService.baseUrlLocal.substring(0, portNumberIndex);
    return {
      floorNumber: data.floor_number,
      floorIndex: data.floor_index,
      elevatorStop: this.elevators.includes(data.floor_index),
      spaces: data.spaces?.map(itemSpaces => {
        if (!!itemSpaces.availability) {
          return entityDetails(
            itemSpaces.availability.id.toString(),
            'Availability',
            `${baseURL}/records/availability/${itemSpaces.availability.id}/edit`
          );
        }
        if (!!itemSpaces.tenant) {
          return entityDetails(
            itemSpaces.tenant.id.toString(),
            'Tenant',
            `${baseURL}/records/tenancy/${itemSpaces.tenant.id}/edit`
          );
        }
        if (!!itemSpaces.non_rentable_space) {
          return entityDetails('NonRentableSpace', 'NonRentableSpace', '');
        }
        if (!!itemSpaces.lease) {
          return entityDetails(
            itemSpaces.lease.id.toString(),
            'Lease',
            `${baseURL}/records/${hasPendingIssue && isTenancyActive() ? 'tenancy' : 'lease'}/${
              itemSpaces.lease.id
            }/edit`
          );
        }
      }),
    };
    function entityDetails(spaceId: string, spaceType: string, url: string) {
      return {
        spaceId,
        spaceType,
        url,
      };
    }
  }

  private mergeConflictSpaces(base: ConflictSpaces[], incoming: ConflictSpaces[]): ConflictSpaces[] {
    return incoming.reduce(
      (result, conflict) => {
        const existingIndex = result.findIndex(
          ({ floorNumber, floorIndex }) => floorNumber === conflict.floorNumber || floorIndex === conflict.floorIndex
        );
        if (existingIndex >= 0) {
          result[existingIndex] = {
            ...result[existingIndex],
            elevatorStop: result[existingIndex].elevatorStop || conflict.elevatorStop,
            spaces: [...(result[existingIndex].spaces ?? []), ...(conflict.spaces ?? [])],
          };
        } else {
          result.push(conflict);
        }
        return result;
      },
      [...base]
    );
  }

  private buildElevatorOnlyFloors(mergedSpaces: ConflictSpaces[], isAboveGround: boolean): ConflictSpaces[] {
    return this.elevators.reduce((resultAcc: ConflictSpaces[], startStop: number) => {
      const alreadyPresent = mergedSpaces.some(
        space => space.floorNumber === startStop.toString() || space.floorIndex === startStop
      );
      const offLimits = isAboveGround ? startStop < this.deleteFloorNumber : startStop > this.deleteFloorNumber;
      if (alreadyPresent || offLimits) {
        return resultAcc;
      }
      return [
        ...resultAcc,
        { floorNumber: startStop.toString(), floorIndex: startStop, elevatorStop: true, spaces: [] },
      ];
    }, []);
  }

  private buildInactiveConflictSpaces(records: any[], entity: EntityType): ConflictSpaces[] {
    const isAvailability = entity === EntityType.Availability;
    const floorMap = new Map<string, Partial<EDPFloorSpaceInfo>>();

    for (const record of records) {
      const id = record?.entity?.id;
      if (id == null) {
        continue;
      }

      const rawSuites = record.floor_suite;
      const floorSuites: any[] = Array.isArray(rawSuites) ? rawSuites : rawSuites != null ? [rawSuites] : [];

      for (const floorSuite of floorSuites) {
        const floorNumber = floorSuite?.floor_number;
        if (floorNumber == null) {
          continue;
        }

        const floorNumberStr = String(floorNumber);
        const matchingFloor = this.stackingPlanService.floors.find(f => String(f.floor_number) === floorNumberStr);
        const floorIndex = matchingFloor?.floor_index ?? Number(floorNumberStr);

        let spaceInfo: Partial<EDPSpaceInfo>;
        if (isAvailability) {
          spaceInfo = { availability: { id } as any };
        } else if (isTenancyActive()) {
          spaceInfo = { tenant: { id } as any };
        } else {
          spaceInfo = { lease: { id } as any };
        }

        if (floorMap.has(floorNumberStr)) {
          floorMap.get(floorNumberStr)!.spaces!.push(spaceInfo as EDPSpaceInfo);
        } else {
          floorMap.set(floorNumberStr, {
            floor_number: floorNumberStr,
            floor_index: floorIndex,
            spaces: [spaceInfo as EDPSpaceInfo],
          });
        }
      }
    }

    return Array.from(floorMap.values()).map(floor => this.generateConflictFloors(floor));
  }
}
